import type { UiNode, UiNodeInputAttributes, UiText } from '@ory/client';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { Spacer, VStack } from '@/components/Layout';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';
import { getOidcProviderIconName } from '@/lib/auth/ory/service';
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  microsoft: 'Microsoft',
  google: 'Google Workspace'
  // apple: "Apple",
};
function getProviderDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAMES[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/**
 * Kratos は context.provider を小文字で返すため、表示用にキャピタライズする。
 * @ory/client の UiText.context は object 型のため、Record にキャストして参照する。
 */
function capitalizeProviderInLabel(label: UiText): UiText {
  const ctx = label.context as Record<string, unknown> | undefined;
  const provider = ctx?.provider;
  if (typeof provider !== 'string') return label;
  return {
    ...label,
    context: {
      ...label.context,
      provider: getProviderDisplayName(provider)
    } as object
  };
}
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * OIDC 専用の submit ボタンコンポーネント。
 * プロバイダー名をキャピタライズし、共通定義からブランドロゴを表示する。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#submit-input-node
 */
export function OidcSubmitInput({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const [wasClicked, setWasClicked] = useState(false);
  const attrs = node.attributes as UiNodeInputAttributes;
  if (attrs.type !== 'submit') {
    return null;
  }
  const labelUiText = node.meta?.label ? capitalizeProviderInLabel(node.meta.label) : undefined;
  const label = labelUiText ? translateUiText(labelUiText) : undefined;
  // provider 識別子（attributes.value、例: "microsoft" / "google"）からロゴを引く。
  const provider = typeof attrs.value === 'string' ? attrs.value : undefined;
  const iconName = provider ? getOidcProviderIconName(provider) : undefined;
  const errorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));

  // OIDC は全画面リダイレクト。押したボタンのみローディング表示にし、
  // 他プロバイダーは独立して押せるままにする。
  const isLoading = wasClicked && !!disabled;
  return <VStack className="w-full gap-0">
      <ActionButton type="submit" name={attrs.name} value={attrs.value as string} disabled={attrs.disabled} isLoading={isLoading} onClick={() => setWasClicked(true)} icon={iconName} variant="default" className="w-full">
        {label}
      </ActionButton>
      {errorMessages && errorMessages.length > 0 && <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages[0]}
          </ResponseMessage>
        </>}
    </VStack>;
}